import { useState } from 'react';
import type { CSSProperties, ChangeEvent } from 'react';
import {
  EDP_ITEM_STATUS_LABELS,
  EDP_PACKAGE_STATUS_LABELS,
  type EdpItemStatus,
  type EdpPackageStatus,
} from '../../shared/employeeDocs.js';
import type { EdpFile, EdpFormField, EdpItem } from '../api/employeeDocsTypes.js';
import { EDP_FIELD_FORMAT_HINTS, type EdpFormIssue } from '../../shared/formValidation.js';
import { fileToBase64 } from '../api/client.js';
import { Alert, Button, Field, Select, TextArea, TextInput, classes } from '../components/ui.js';
import { formatBytes, formatDateTime } from '../lib/format.js';

const PACKAGE_STYLES: Record<EdpPackageStatus, string> = {
  invited: 'bg-slate-100 text-slate-800 ring-slate-300',
  in_progress: 'bg-sky-50 text-sky-800 ring-sky-300',
  submitted: 'bg-amber-50 text-amber-900 ring-amber-300',
  corrections_requested: 'bg-orange-50 text-orange-900 ring-orange-300',
  released: 'bg-emerald-50 text-emerald-800 ring-emerald-300',
  usps_returned: 'bg-red-50 text-red-900 ring-red-300',
  cancelled: 'bg-slate-100 text-slate-600 ring-slate-300',
};

/** A submitted package on which a reviewer has already decided some items. */
export function packageStatusLabel(status: EdpPackageStatus, reviewStarted = false): string {
  return status === 'submitted' && reviewStarted
    ? 'QA review in progress'
    : EDP_PACKAGE_STATUS_LABELS[status];
}

export function PackageStatusBadge({
  status,
  reviewStarted = false,
}: {
  status: EdpPackageStatus;
  reviewStarted?: boolean;
}) {
  return (
    <span
      className={classes(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset',
        PACKAGE_STYLES[status],
      )}
    >
      {packageStatusLabel(status, reviewStarted)}
    </span>
  );
}

const ITEM_STYLES: Record<EdpItemStatus, string> = {
  pending: 'bg-slate-100 text-slate-700 ring-slate-300',
  completed: 'bg-sky-50 text-sky-800 ring-sky-300',
  accepted: 'bg-emerald-50 text-emerald-800 ring-emerald-300',
  rejected: 'bg-red-50 text-red-800 ring-red-300',
};

export function ItemStatusBadge({ status }: { status: EdpItemStatus }) {
  return (
    <span
      className={classes(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset',
        ITEM_STYLES[status],
      )}
    >
      {EDP_ITEM_STATUS_LABELS[status]}
    </span>
  );
}

export function RejectionNote({ item }: { item: EdpItem }) {
  if (item.status !== 'rejected') return null;
  return (
    <Alert
      variant="warning"
      title={`${item.lastRejectionBy === 'usps' ? 'Returned by USPS' : 'Returned for correction'}: ${item.lastRejectionReasonLabel ?? 'see note'}`}
    >
      {item.lastRejectionComment && <p className="mt-1">{item.lastRejectionComment}</p>}
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Form field renderer (driven by the fields discovered in the fillable PDF)
// ---------------------------------------------------------------------------

export type FormValues = Record<string, string | boolean>;

// Staff-only fields are never sent to the employee; without an explicit mode the
// server treats text-like fields as required and checkboxes as optional.
function visibleMode(field: EdpFormField): 'required' | 'optional' | 'staff' {
  return field.mode ?? (field.type === 'checkbox' && !field.required ? 'optional' : 'required');
}

/**
 * Lays out a form's fields: consecutive fields sharing a `section` are shown together
 * under one heading (e.g. one row of an address-history table) with a single
 * optional/required note, instead of one full-width box and note per field.
 */
export function FormFieldGrid({
  fields,
  values,
  disabled,
  issues = [],
  onChange,
}: {
  fields: readonly EdpFormField[];
  values: FormValues;
  disabled?: boolean;
  /** What is wrong with the answers; shown in red next to the box or under the group heading. */
  issues?: readonly EdpFormIssue[];
  onChange: (name: string, value: string | boolean) => void;
}) {
  const errorFor = (name: string) => issues.find((issue) => issue.field === name)?.message;
  const visible = fields.filter((field) => visibleMode(field) !== 'staff');
  const blocks: { section?: string; fields: EdpFormField[] }[] = [];
  for (const field of visible) {
    const last = blocks[blocks.length - 1];
    if (last && last.section === field.section) last.fields.push(field);
    else blocks.push({ section: field.section, fields: [field] });
  }
  // A section's directions (e.g. "Leave no gaps…") are shown once, above the first of
  // consecutive groups that carry the same wording, such as the rows of one table.
  let previousSectionInstructions: string | undefined;
  return (
    <div className="flex flex-col gap-4">
      {blocks.map((block, index) => {
        if (!block.section) {
          previousSectionInstructions = undefined;
          return (
            <div key={index} className="grid gap-4 sm:grid-cols-2">
              {block.fields.map((field) => (
                <div
                  key={field.name}
                  className={field.multiline || field.instructions ? 'sm:col-span-2' : undefined}
                >
                  <FormFieldInput
                    field={field}
                    value={values[field.name]}
                    disabled={disabled}
                    error={errorFor(field.name)}
                    onChange={(value) => onChange(field.name, value)}
                  />
                </div>
              ))}
            </div>
          );
        }
        const allOptional = block.fields.every((field) => visibleMode(field) === 'optional');
        const columns = Math.min(block.fields.length, 7);
        const instructions = block.fields.find((field) => field.instructions)?.instructions;
        const showInstructions = instructions && instructions !== previousSectionInstructions;
        previousSectionInstructions = instructions;
        const sectionIssues = issues.filter(
          (issue) => !issue.field && issue.section === block.section,
        );
        return (
          <fieldset
            key={index}
            className="rounded-md border border-slate-200 bg-slate-50/60 px-3 pb-3 pt-2"
          >
            <legend className="px-1 text-sm font-semibold text-slate-800">
              {block.section}
              {allOptional && (
                <span className="ml-2 font-normal text-slate-500">
                  Optional – complete only if it applies to you
                </span>
              )}
            </legend>
            {showInstructions && <FormDirections text={instructions} className="mb-3" />}
            {sectionIssues.map((issue) => (
              <p key={issue.message} role="alert" className="mb-2 text-sm font-medium text-red-700">
                {issue.message}
              </p>
            ))}
            <div
              className="grid grid-cols-2 items-end gap-3 md:[grid-template-columns:var(--cols)]"
              style={
                { ['--cols' as string]: `repeat(${columns}, minmax(0, 1fr))` } as CSSProperties
              }
            >
              {block.fields.map((field) => (
                <FormFieldInput
                  key={field.name}
                  field={field}
                  value={values[field.name]}
                  disabled={disabled}
                  compact
                  error={errorFor(field.name)}
                  onChange={(value) => onChange(field.name, value)}
                />
              ))}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}

/** The form's own directions for a question or section, quoted for the employee. */
export function FormDirections({ text, className }: { text: string; className?: string }) {
  return (
    <p
      className={`whitespace-pre-line rounded-sm border-l-4 border-slate-300 bg-slate-100 px-3 py-1.5 text-sm italic text-slate-700 ${className ?? ''}`}
    >
      {text}
    </p>
  );
}

export function FormFieldInput({
  field,
  value,
  onChange,
  disabled,
  compact,
  error,
}: {
  field: EdpFormField;
  value: string | boolean | undefined;
  onChange: (value: string | boolean) => void;
  disabled?: boolean;
  /** Inside a section: no per-field "Optional" note (the section carries it). */
  compact?: boolean;
  error?: string;
}) {
  const id = `field-${field.name}`;
  const mode = visibleMode(field);
  if (mode === 'staff') return null;
  const placeholder = field.format ? EDP_FIELD_FORMAT_HINTS[field.format] : undefined;
  const isRequired = mode === 'required';
  const optionalHint = compact ? undefined : 'Optional';
  // Inside a section the directions belong to the group and are shown once by the grid.
  const directions =
    !compact && field.instructions ? (
      <FormDirections text={field.instructions} className="mb-2" />
    ) : null;
  if (field.type === 'checkbox') {
    return (
      <div>
        {directions}
        <label className="flex items-start gap-2 text-sm text-slate-800">
          <input
            id={id}
            type="checkbox"
            className="mt-0.5"
            checked={value === true}
            disabled={disabled}
            onChange={(event) => onChange(event.target.checked)}
          />
          <span>
            {field.label}
            {isRequired && <span className="text-red-700"> *</span>}
          </span>
        </label>
        {error && (
          <p role="alert" className="text-xs font-medium text-red-700">
            {error}
          </p>
        )}
      </div>
    );
  }
  if (field.type === 'choice' || field.type === 'radio') {
    const options = field.options ?? [];
    return (
      <Field
        label={field.label}
        required={isRequired}
        hint={isRequired ? undefined : optionalHint}
        error={error}
      >
        {directions}
        <div role="radiogroup" aria-label={field.label} className="flex flex-wrap gap-x-5 gap-y-1">
          {options.map((option) => (
            <label key={option} className="flex items-center gap-2 text-sm text-slate-800">
              <input
                type="radio"
                name={id}
                value={option}
                checked={value === option}
                disabled={disabled}
                onChange={() => onChange(option)}
              />
              {option}
            </label>
          ))}
        </div>
      </Field>
    );
  }
  if (field.type === 'dropdown') {
    return (
      <Field label={field.label} htmlFor={id} required={isRequired} error={error}>
        {directions}
        <Select
          id={id}
          aria-invalid={error ? true : undefined}
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Select…</option>
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      </Field>
    );
  }
  return (
    <Field
      label={field.label}
      htmlFor={id}
      required={isRequired}
      hint={
        field.presetValue
          ? `Leave blank to use: ${field.presetValue}`
          : field.format
            ? `Example: ${EDP_FIELD_FORMAT_HINTS[field.format]}`
            : isRequired
              ? undefined
              : optionalHint
      }
      error={error}
    >
      {directions}
      {field.multiline ? (
        <TextArea
          id={id}
          rows={3}
          value={typeof value === 'string' ? value : ''}
          placeholder={field.presetValue}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <TextInput
          id={id}
          value={typeof value === 'string' ? value : ''}
          placeholder={field.presetValue ?? placeholder}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          className={error ? 'ring-red-500 focus:ring-red-600' : undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}

// ---------------------------------------------------------------------------
// File list and picker
// ---------------------------------------------------------------------------

export function FileList({
  files,
  hrefFor,
  onRemove,
  removing,
}: {
  files: EdpFile[];
  hrefFor: (file: EdpFile) => string;
  onRemove?: (file: EdpFile) => void;
  removing?: boolean;
}) {
  if (files.length === 0) return <p className="text-sm text-slate-500">No files yet.</p>;
  return (
    <ul className="divide-y divide-slate-200 rounded-md ring-1 ring-slate-200">
      {files.map((file) => (
        <li
          key={file.id}
          className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
        >
          <div className="min-w-0">
            <a
              href={hrefFor(file)}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-brand-700 hover:underline"
            >
              {file.fileName}
            </a>
            <p className="text-xs text-slate-500">
              {formatBytes(file.byteSize)} · {formatDateTime(file.uploadedAt)}
              {file.isRenderedForm && ' · completed form'}
              {file.isSignedScan && ' · signed copy (ink)'}
            </p>
          </div>
          {onRemove && (
            <Button
              variant="ghost"
              className="text-red-700"
              loading={removing}
              onClick={() => onRemove(file)}
            >
              Remove
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}

export interface PickedFile {
  fileName: string;
  mimeType: string;
  contentBase64: string;
}

/** Reads a chosen file into the JSON upload shape. Accepts PDF only. */
export function FilePicker({
  label,
  onPick,
  disabled,
  accept = 'application/pdf',
}: {
  label: string;
  onPick: (file: PickedFile) => Promise<void> | void;
  disabled?: boolean;
  accept?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await onPick({
        fileName: file.name,
        mimeType: file.type,
        contentBase64: await fileToBase64(file),
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The file could not be uploaded.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <label
        className={classes(
          'inline-flex w-fit cursor-pointer items-center gap-2 rounded-md bg-white px-3.5 py-2 text-sm font-semibold text-slate-800 ring-1 ring-inset ring-slate-300 hover:bg-slate-50',
          (disabled || busy) && 'cursor-not-allowed opacity-50',
        )}
      >
        {busy ? 'Uploading…' : label}
        <input
          type="file"
          className="sr-only"
          accept={accept}
          disabled={disabled || busy}
          onChange={(event) => void handleChange(event)}
        />
      </label>
      {error && <Alert variant="error">{error}</Alert>}
    </div>
  );
}
